import { catchError, filter, finalize, first, of, switchMap, takeUntil, tap, timeout } from 'rxjs';
import { AppService } from './core/services/app.service';
import { MigrateClassificationRulesToTcvGQL, Organization, OrganizationBySubdomainGQL, OrganizationRole } from '../../graphql/generated';
import { SubdomainResolver } from './core/resolvers/subdomain.resolver';
import { inject } from '@angular/core';
import { AppContext } from './core/services/app-context.service';
import { map } from 'rxjs/operators';
import { CacheService } from '@knalgeel/pandora';
import { BreadcrumbService } from './core/services/breadcrumb.service';
import { awaitLoading } from './shared/rxjs/await-loading';
import { CustomAuthService } from './core/services/custom-auth.service';
import { MemberService } from './core/services/member.service';
import { isTotalEstablishment, TOTAL_ESTABLISHMENT } from './core/services/establishment-scope';

export function initializeOrganization() {
    const organizationBySubdomainGQL = inject(OrganizationBySubdomainGQL);
    const subdomainResolver = inject(SubdomainResolver);
    const appContext = inject(AppContext);
    const authService = inject(CustomAuthService);

    return () => {
        console.log('[initializeOrganization] START');
        return of(subdomainResolver.resolve()).pipe(
        tap(() => appContext.set('status', 'initializing')),
        switchMap(subdomain => {
            // If no subdomain (root domain), skip organization initialization
            if (!subdomain) {
                appContext.set('status', 'initialized');
                return of(null);
            }

            // Wait for auth loading to complete before checking authentication
            return authService.isLoading$.pipe(
                filter(loading => !loading),
                first(),
                switchMap(() => authService.isAuthenticated$),
                first(),
                switchMap(isAuthenticated => {
                    // If not authenticated, skip organization loading and mark as initialized
                    // The route guard will handle redirecting to unauthorized page
                    if (!isAuthenticated) {
                        console.log('User not authenticated, skipping organization initialization');
                        appContext.set('status', 'initialized');
                        return of(null);
                    }

                    // User is authenticated, proceed with organization loading
                    return organizationBySubdomainGQL.watch({ subdomain }).valueChanges.pipe(
                        awaitLoading(),
                        map(response => response.data.organizationBySubdomain as Organization),
                        tap(organization => {
                            if (organization) {
                                appContext.set('organizationId', organization.id);
                            } else {
                                console.warn('Organization not found for subdomain:', subdomain);
                                appContext.set('status', 'error');
                            }
                        }),
                        first(),
                    );
                }),
            );
        }),
        catchError((error) => {
            console.error('[initializeOrganization] ERROR:', error);
            // Mark as initialized even on error so the app can load and route guards can handle auth
            appContext.set('status', 'initialized');
            return of(null);
        }),
        finalize(() => console.log('[initializeOrganization] COMPLETE'))
    );
    };
}

export function initializeEstablishment() {
    const appContext = inject(AppContext);
    const appService = inject(AppService);
    const cacheService = inject(CacheService);
    const subdomainResolver = inject(SubdomainResolver);
    const authService = inject(CustomAuthService);
    const memberService = inject(MemberService);
    const migrateClassificationRulesToTcvGQL = inject(MigrateClassificationRulesToTcvGQL);

    return () => {
        console.log('[initializeEstablishment] START');
        // Skip establishment initialization on root domain (no subdomain)
        const subdomain = subdomainResolver.resolve();
        if (!subdomain) {
            console.log('[initializeEstablishment] No subdomain, skipping');
            return of(null);
        }

        // Wait for auth loading to complete before checking authentication
        return authService.isLoading$.pipe(
            filter(loading => !loading),
            first(),
            switchMap(() => authService.isAuthenticated$),
            first(),
            switchMap(isAuthenticated => {
                // If not authenticated, skip establishment initialization
                if (!isAuthenticated) {
                    console.log('[initializeEstablishment] User not authenticated, skipping');
                    return of(null);
                }

                console.log('[initializeEstablishment] Waiting for accessible establishments...');
                return appService.accessibleEstablishments$.pipe(
            // For agents, establishments may be empty — use a timeout to avoid hanging
            timeout(3000),
            catchError(() => {
                console.log('[initializeEstablishment] Timeout waiting for establishments (agent?)');
                return of([]);
            }),
            first(establishments => {
                // Allow through if we have establishments OR if the user is an agent
                const member = memberService['_currentMember$'].value;
                if (member?.role === OrganizationRole.Agent) {
                    return true;
                }
                return establishments.length > 0;
            }),
            map(establishments => {
                if (establishments.length === 0) return null;

                const cachedEstablishmentId = cacheService.get('establishmentId');

                // Totaal is not one of the establishments, so looking it up would fall through to
                // the first one and silently drop you out of the totals view on every reload.
                // The API restricts a totals request to what the caller may see either way, so
                // honouring the cached choice here cannot widen anyone's access.
                if (isTotalEstablishment(cachedEstablishmentId)) {
                    return TOTAL_ESTABLISHMENT;
                }

                return establishments.find(establishment => establishment.id === cachedEstablishmentId)
                    || establishments[0];
            }),
            tap(establishment => {
                console.log('[initializeEstablishment] Got establishment:', establishment?.id);
                if (establishment) {
                    appContext.set('establishmentId', establishment.id);
                    migrateClassificationRulesToTcvGQL.mutate().subscribe({
                        error: () => {
                            // Best-effort migration on boot; a failure here must not
                            // block the app from starting.
                        }
                    });
                }
            }),
            first(),
                    finalize(() => {
                        console.log('[initializeEstablishment] COMPLETE (with organization)');
                        appContext.set('status', 'initialized');
                    }),
                    takeUntil(appContext.error$),
                );
            }),
            finalize(() => console.log('[initializeEstablishment] COMPLETE'))
        );
    };
}

export function initializeBreadcrumbs() {
    const breadcrumbService = inject(BreadcrumbService);

    return () => {
        console.log('[initializeBreadcrumbs] START');
        const result = breadcrumbService.init();
        console.log('[initializeBreadcrumbs] COMPLETE');
        return result;
    };
}

export function initializeApp() {
    const appService = inject(AppService);
    const appContext = inject(AppContext);
    const subdomainResolver = inject(SubdomainResolver);
    const authService = inject(CustomAuthService);

    return () => {
        // Skip app initialization on root domain (no subdomain)
        const subdomain = subdomainResolver.resolve();
        if (!subdomain) {
            // Mark as initialized for root domain
            console.log('[initializeApp] Root domain, marking as initialized');
            appService.markAsInitialized();
            return of(null);
        }

        // Wait for auth loading to complete before checking authentication
        return authService.isLoading$.pipe(
            filter(loading => !loading),
            first(),
            switchMap(() => authService.isAuthenticated$),
            first(),
            switchMap(isAuthenticated => {
                // If not authenticated, mark as initialized and let route guards handle redirect
                if (!isAuthenticated) {
                    console.log('[initializeApp] User not authenticated, skipping app initialization and marking as initialized');
                    appService.markAsInitialized();
                    return of(null);
                }

                // User is authenticated, proceed with normal initialization
                console.log('[initializeApp] User authenticated, proceeding with normal init');
                return appService.init().pipe(
                    takeUntil(appContext.error$),
                    finalize(() => console.log('[initializeApp] COMPLETE (authenticated)'))
                );
            }),
            finalize(() => console.log('[initializeApp] COMPLETE'))
        );
    };
}

export function initializeMember() {
    const memberService = inject(MemberService);
    const subdomainResolver = inject(SubdomainResolver);
    const authService = inject(CustomAuthService);
    const appContext = inject(AppContext);

    return () => {
        const subdomain = subdomainResolver.resolve();
        if (!subdomain) {
            console.log('[initializeMember] No subdomain, skipping');
            return of(null);
        }

        return authService.isLoading$.pipe(
            filter(loading => !loading),
            first(),
            switchMap(() => authService.isAuthenticated$),
            first(),
            switchMap(isAuthenticated => {
                if (!isAuthenticated) {
                    console.log('[initializeMember] Not authenticated, skipping');
                    return of(null);
                }

                // Wait for organizationId to be set (runs in parallel with initializeOrganization)
                console.log('[initializeMember] Waiting for organizationId...');
                return appContext.organizationId$.pipe(
                    filter(id => !!id),
                    first(),
                    timeout(5000),
                    catchError(() => {
                        console.warn('[initializeMember] Timeout waiting for organizationId');
                        return of(null);
                    }),
                    filter(id => !!id),
                    switchMap(() => {
                        console.log('[initializeMember] Loading current member...');
                        return memberService.loadCurrentMember().pipe(
                            tap(member => console.log('[initializeMember] Loaded member:', member?.role)),
                            finalize(() => console.log('[initializeMember] COMPLETE')),
                        );
                    }),
                );
            }),
        );
    };
}
